import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Cliente, ClienteCoincidencia, ClientesService, MotivoCoincidencia } from './clientes.service';

export type ResultadoCoincidencias =
  | { accion: 'usar'; cliente: Cliente }
  | { accion: 'crear' }
  | { accion: 'cancelar' };

interface CoincidenciasState {
  open: boolean;
  items: ClienteCoincidencia[];
  usando: string | null;
}

export const MOTIVO_COINCIDENCIA: Record<MotivoCoincidencia, string> = {
  telefono: 'Mismo teléfono',
  email: 'Mismo email',
  rfc: 'Mismo RFC',
  placas: 'Mismas placas',
  nombre: 'Mismo nombre',
};

/**
 * Aviso de "este cliente ya existe" antes de dar de alta.
 *
 * La cartera es del taller: un cliente dado de alta en una sucursal se atiende
 * en cualquier otra. Antes de crear uno nuevo se buscan los que ya tienen su
 * teléfono, email, RFC, placas o nombre completo (en cualquier sucursal) y, si
 * hay, se ofrece usar el existente. El diálogo vive en `app.ts` como un
 * <dialog>.showModal() real para quedar encima del modal que lo invoca.
 *
 * Si la búsqueda falla no se bloquea el alta: se resuelve como 'crear'.
 */
@Injectable({ providedIn: 'root' })
export class ClienteCoincidenciasService {
  private readonly clientes = inject(ClientesService);
  state = signal<CoincidenciasState>({ open: false, items: [], usando: null });
  private resolver: ((r: ResultadoCoincidencias) => void) | null = null;

  async revisar(datos: Partial<Cliente> & { placas?: string }, excluir?: string): Promise<ResultadoCoincidencias> {
    let items: ClienteCoincidencia[] = [];
    try {
      items = await firstValueFrom(this.clientes.buscarCoincidencias(datos, excluir));
    } catch {
      return { accion: 'crear' };
    }
    if (!items.length) return { accion: 'crear' };

    this.resolver?.({ accion: 'cancelar' });
    return new Promise<ResultadoCoincidencias>((resolve) => {
      this.resolver = resolve;
      this.state.set({ open: true, items, usando: null });
    });
  }

  /** Usa el cliente existente; trae su ficha completa (crédito, flotilla…). */
  async usar(c: ClienteCoincidencia): Promise<void> {
    this.state.update(s => ({ ...s, usando: c.id }));
    let cliente: Cliente;
    try {
      cliente = await firstValueFrom(this.clientes.getById(c.id));
    } catch {
      cliente = c as Cliente;
    }
    this.cerrar({ accion: 'usar', cliente });
  }

  crear(): void {
    this.cerrar({ accion: 'crear' });
  }

  cancelar(): void {
    this.cerrar({ accion: 'cancelar' });
  }

  motivo(m: MotivoCoincidencia): string {
    return MOTIVO_COINCIDENCIA[m] || m;
  }

  private cerrar(r: ResultadoCoincidencias): void {
    this.state.set({ open: false, items: [], usando: null });
    const res = this.resolver;
    this.resolver = null;
    res?.(r);
  }
}
